// @ts-nocheck
// @flow
import { useEffect, useState } from 'react'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { ArticleData } from '@ta-interaktiv/react-article-teasers'
import {
  getAuthors,
  getLead,
  getPublishingDate,
  getTeaserImgSrc,
  getTitle,
  getTitleHeader,
  getUpdatedDate,
} from '@ta-interaktiv/api-access'
import styled from 'styled-components'

import { useGesture } from '@use-gesture/react'
import { useSpring, animated } from '@react-spring/web'

import { GlobalVariables } from 'Globals/GlobalVariables'
import useIsMobile from 'hooks/useIsMobile'

type Timestamp = number

interface ArticleTeaserData {
  author: string[]
  imageSrc: string
  lead: string
  title: string
  titleHeader: string
  publishingDate: Timestamp
  updateDate: Timestamp
  id: string | number
}

/**
 * Dynamic inline teaser Function.
 * This function allows to render one or several article teasers.
 *
 * @param {string} title The title displayed on the bloc containing the teasers. "Lire aussi:" is the default value.
 * @param {string[]} ids The list of articles' ids.
 */
export const ArticleTeaser = ({
  title,
  ids,
  inverted,
  handleClick,
  handleArticleLoaded,
  readArticles,
}: {
  title?: string
  ids: string[]
  inverted: boolean
  handleClick: (id: string | number) => void
  handleArticleLoaded: () => void
  readArticles: number[] | string[]
}) => {
  const [limit, setLimit] = useState(4)
  const isMobile = useIsMobile()
  const domain =
    parseHostname().publicationName === 'tagesanzeiger'
      ? '24heures'
      : parseHostname().publicationName
  const [articles, setArticles] = useState(
    new Map<string | number, ArticleData>()
  )
  const [page, setPage] = useState(0)
  const [allowDrag, setAllowDrag] = useState(false)
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null)

  const getArticleTeaserData = (
    domain: string,
    articleId: string | number
  ): Promise<ArticleTeaserData> => {
    return Promise.all([
      getAuthors(domain, articleId),
      getTeaserImgSrc(domain, articleId, '800px'),
      getLead(domain, articleId),
      getTitle(domain, articleId),
      getTitleHeader(domain, articleId),
      getPublishingDate(domain, articleId),
      getUpdatedDate(domain, articleId),
    ])
      .then(
        ([
          authors,
          teaserImgSrc,
          lead,
          title,
          titleHeader,
          publishingDateAsIsoString,
          updatedDateAsIsoString,
        ]: [string[], string, string, string, string, string, string]) => {
          return {
            authors: authors.join(', '),
            imageSrc: teaserImgSrc,
            lead: lead ?? '',
            title: title ?? '',
            titleHeader: titleHeader,
            publishingDate: new Date(publishingDateAsIsoString).getTime(),
            updateDate: new Date(updatedDateAsIsoString).getTime(),
            id: articleId,
          }
        }
      )
      .catch((e: Error) => {
        console.warn(
          `Article with ID ${articleId} could not be loaded, due to ${e.name}: ${e.message}`
        )
      })
  }
  const [{ x }, api] = useSpring(() => ({ x: 0 }))
  const titleSlug = title.replace(/ /g, '-')
  const bind = useGesture(
    {
      onDrag: ({ down, swipe: [swipeX], movement: [mx, my] }) => {
        // 65 / 100 devait correspondre à la largeur de l'image de teaser par rapport
        // à innerWidth
        // Probleme: innerWidth est beaucoup trop grand et fausse le calcul
        // => on utilise une ref sur les Teasers
        const releasePos = page * -contentRef?.offsetWidth
        if (allowDrag) {
          if (swipeX !== 0) {
            setAllowDrag(false)
            const newPage = page + -swipeX
            if (
              (page > 0 && swipeX > 0) ||
              (page < articles.size - 1 && swipeX < 0)
            ) {
              setPage(newPage)
            }
          }
        }

        if (allowDrag) {
          api.start({ x: down ? mx + releasePos : releasePos, immediate: down })
        }
      },
      onDragStart: (state) => {
        setAllowDrag(true)
      },
      onDragEnd: (state) => {
        setAllowDrag(true)
      },
    },
    {
      drag: {
        swipe: {
          distance: [40, 40], // default: [50, 50]
          duration: 150, // default: 250
        },
      },
    }
  )

  useEffect(() => {
    if (articles.size > 0) {
      const releasePos = page * -contentRef?.offsetWidth
      api.start({ x: releasePos })
    }
  }, [page])

  useEffect(() => {
    ids.forEach((articleId) => {
      getArticleTeaserData(domain, articleId).then(
        (articleTeaserData: ArticleData) => {
          if (articleTeaserData) {
            setArticles(
              (articles) =>
                new Map(
                  [...articles.set(articleId, articleTeaserData)].sort(
                    (a, b) => b[1].updateDate - a[1].updateDate
                  )
                )
            )
            handleArticleLoaded(articleTeaserData)
          }
        }
      )
    })
  }, [])

  return (
    <>
      {articles.size > 0 &&
        (isMobile ? (
          <Container>
            <Content>
              <SectionHeader inverted={inverted}>{title}</SectionHeader>
              <ArticleTitle inverted={inverted}>
                <ArticleNum>{page + 1}</ArticleNum>
                <a
                  onClick={(e) => {
                    e.preventDefault()
                    handleClick([...articles.values()][page].id)
                    window.open(
                      `https://www.${domain}.ch/${
                        [...articles.values()][page].id
                      }`,
                      '_blank'
                    )
                  }}
                  href={`https://www.${domain}.ch/${
                    [...articles.values()][page].id
                  }`}
                >
                  {[...articles.values()][page].title}
                </a>
                <div>
                  <a
                    onClick={(e) => {
                      e.preventDefault()
                      handleClick([...articles.values()][page].id)
                      window.open(
                        `https://www.${domain}.ch/${
                          [...articles.values()][page].id
                        }`,
                        '_blank'
                      )
                    }}
                    href={`https://www.${domain}.ch/${
                      [...articles.values()][page].id
                    }`}
                  >
                    <Button inverted={inverted}>
                      {readArticles.includes(
                        [...articles.values()][page].id
                      ) ? (
                        <>
                          Déjà lu{' '}
                          <svg
                            width='16px'
                            height='16px'
                            viewBox='0 0 16 16'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              fill='none'
                              strokeWidth='1.5'
                              stroke='#ffffff'
                              d='m3 8 4 4 6-9'
                            />
                          </svg>
                        </>
                      ) : (
                        <>Lire l'article</>
                      )}
                    </Button>
                  </a>
                </div>
              </ArticleTitle>
              <List>
                <animated.div className='animated' {...bind()} style={{ x }}>
                  {[...articles.values()].map((article: ArticleData) => (
                    <TeaserContainer
                      ref={setContentRef}
                      key={`${titleSlug}-article-teaser-${article.id}`}
                      href={`https://www.${domain}.ch/${article.id}`}
                      onClick={(e) => {
                        e.preventDefault()

                        if ([...articles.values()][page].id === article.id) {
                          console.log('OK')
                        } else {
                          setPage(page + 1)
                          return 0
                        }
                        handleClick([...articles.values()][page].id)
                        window.open(
                          `https://www.${domain}.ch/${article.id}`,
                          '_blank'
                        )
                      }}
                    >
                      <ImageContainer>
                        <Image alt='' src={article.imageSrc} />
                      </ImageContainer>
                    </TeaserContainer>
                  ))}
                </animated.div>
              </List>
              <PaginationHelp inverted={inverted}>
                ◄ Swipez pour en voir plus ►
              </PaginationHelp>
              <Pagination>
                <div className='pagination-line-content'>
                  <div
                    className='pagination-line'
                    style={{
                      width:
                        ((page + 1) / [...articles.values()].length) * 100 +
                        '%',
                    }}
                  />
                </div>
              </Pagination>
            </Content>
          </Container>
        ) : (
          <Container>
            <SectionHeader inverted={inverted}>{title}</SectionHeader>
            <GridContainer inverted={inverted}>
              {[...articles.values()]
                .slice(0, limit)
                .map((article: ArticleData) => (
                  <CardContainer
                    key={`${titleSlug}-article-teaser-${article.id}`}
                  >
                    <a
                      href={`https://www.${domain}.ch/${article.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        handleClick(article.id)
                        window.open(
                          `https://www.${domain}.ch/${article.id}`,
                          '_blank'
                        )
                      }}
                    >
                      <ImageContainer>
                        <Image alt='' src={article.imageSrc} />
                      </ImageContainer>
                      <CardContent>
                        <ArticleTitle inverted={inverted}>
                          {article.title}
                        </ArticleTitle>
                      </CardContent>
                      <ButtonContainer>
                        <Button inverted={inverted}>
                          {readArticles.includes(+article.id) ? (
                            <>
                              Déjà lu{' '}
                              <svg
                                width='16px'
                                height='16px'
                                viewBox='0 0 16 16'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  fill='none'
                                  strokeWidth='1.5'
                                  d='m3 8 4 4 6-9'
                                />
                              </svg>
                            </>
                          ) : (
                            <>Lire l'article</>
                          )}
                        </Button>
                      </ButtonContainer>
                    </a>
                  </CardContainer>
                ))}
            </GridContainer>
            {limit < [...articles.values()].length && !inverted && (
              <div
                className='ui vertical very fitted segment'
                style={{ paddingBottom: 0 }}
              >
                <div className='ui center aligned text container'>
                  <Button
                    className='big'
                    onClick={() => {
                      setLimit(limit + 4)
                    }}
                  >
                    Plus de questions
                  </Button>
                </div>
              </div>
            )}
          </Container>
        ))}
    </>
  )
}

const Content = styled.div`
  padding: 2em 0 0 0;

  @media screen and (min-width: 768px) {
    padding: 0 5% 1em 5%;
  }
`

const List = styled.div`
  .animated {
    padding-bottom: 0.5em;
    overflow: visible;
    width: 100vw;
    margin-top: 1em;
    touch-action: pan-y;
    display: flex;
    flex-direction: row;
    position: relative;
    counter-reset: question;
  }
`

const Pagination = styled.div`
  text-align: center;

  .pagination-line-content {
    width: 25%;
    background-color: ${({ theme }) => theme.paginationLineContent};
    border-radius: 20px;
    overflow: hidden;
    height: 14px;
    position: relative;
    display: inline-block;
  }

  .pagination-line {
    background-color: ${({ theme }) => theme.mainColor};
    ${({ inverted }) =>
      inverted && 'background-color: var(--stda-light-yellow);'}
    border-radius: 20px;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
  }
`

const PaginationHelp = styled.div<{ inverted?: boolean }>`
  line-height: 1.15em;
  font-weight: 400;
  padding-bottom: 0.55em;
  font-size: 0.55em;
  color: ${({ theme }) => theme.mainColor};
  ${({ inverted }) => inverted && 'color: var(--stda-light-yellow);'}
  text-transform: uppercase;
  text-align: center;
  -webkit-font-smoothing: auto;
  letter-spacing: 0.1em;
  opacity: 0.65;
`

const TeaserContainer = styled.a`
  display: flex;
  flex: 1 0 65%;
`

const GridContainer = styled(GlobalVariables)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 10px;
  gap: 10px;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 12px;
    gap: 12px;
  }
`

const CardContainer = styled.div`
  overflow: hidden;
  position: relative;
  text-align: center;
  font-weight: 800;
  font-size: 1em;
  cursor: pointer;
  transition: transform 100ms ease-in-out;
  -webkit-font-smoothing: auto;
  line-height: 1.2;

  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
  }

  a {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`

const ArticleNum = styled.div`
  position: absolute;
  top: 0.2em;
  left: 0;
  font-size: 0.65em;
  font-weight: 800;
  display: none;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-top: 1rem;
  padding: 0 5% 0 5%;
`

const Container = styled.div`
  display: block;
  padding: 1em 0 2em 0;
`

const ImageContainer = styled.div`
  width: 90%;
  height: auto;
  margin-right: 1em;
  overflow: hidden;
  position: relative;
  border-radius: 12px;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
  background-color: white;
  padding: 10px;

  &:after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  @media screen and (max-width: 768px) {
    margin-bottom: 0.25em;
  }
`

const Image = styled.img`
  width: 100%;
  height: auto;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  mix-blend-mode: multiply;
`

const SectionHeader = styled.div<{ inverted?: boolean }>`
  line-height: 1.15em;
  font-weight: 400;
  padding-bottom: 0.55em;
  font-size: 1em;
  color: ${({ theme }) => theme.mainColor};
  ${({ inverted }) =>
    inverted &&
    `
    color: var(--stda-light-yellow);
  `}
  display: block;
  position: relative;

  @media only screen and (min-width: 768px) {
    margin-top: 2em;
    text-transform: none;
    letter-spacing: normal;
    font-size: 1.8em;
    opacity: 1;

    font-size: 1.25em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.5em;
  }
`

const ArticleTitle = styled.div<{ inverted?: boolean }>`
  position: relative;
  line-height: 1.2;
  margin-top: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.mainColor};
  ${({ inverted }) => inverted && 'color: var(--stda-light-yellow);'}
  font-family: var(--font-noto);
  text-align: left;
  a {
    color: ${({ theme }) => theme.mainColor} !important;
    ${({ inverted }) =>
      inverted && 'color: var(--stda-light-yellow) !important;'}
  }
`

const ButtonContainer = styled.div`
  text-align: left;
  margin-top: auto;
  padding: 0 5%;
`

const Button = styled.div<{ inverted?: boolean }>`
  display: inline-block;
  line-height: 1em;
  border: 1px solid ${({ theme }) => theme.mainColor};
  color: ${({ theme }) => theme.mainColor};
  ${({ inverted }) =>
    inverted &&
    `
    border-color: var(--stda-light-yellow);
    color: var(--stda-light-yellow);
  `}
  border-radius: 40px;
  padding: 0.7em 1.4em 0.8em 1.4em;
  font-size: 1.2rem;
  margin-top: 1em;
  font-family: var(--font-plex);
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  font-weight: 400;
  letter-spacing: 0.02em;
  -webkit-font-smoothing: auto;

  @media only screen and (min-width: 768px) {
    background-color: transparent;
    -webkit-font-smoothing: auto;
    font-weight: 400;
    margin-top: 1em;
  }
  svg {
    margin-left: 0.35em;
  }
  svg path {
    stroke: ${(theme) => theme.mainColor};
    ${({ theme }) =>
      theme.darkMode &&
      `
      stroke: var(--stda-light-yellow) !important;
      `}
    ${({ inverted }) =>
      inverted &&
      `
      stroke: var(--stda-light-yellow);
     `}
  }

  &.big {
    background-color: var(--stda-main-color);
    color: #f1e2ce;
    ${({ theme }) =>
      theme.darkMode &&
      `
      // Orange variant
      background-color: var(--stda-secondary-color);
      border: none;
      color: #000;

      // Light yellow variant
      background-color: var(--stda-light-yellow);
      border: none;
      color: ${theme.backgroundColor};
      `}

    font-size: 1.5em;
  }
`
